import {makeObservable, action, observable} from 'mobx';
import {type ICategory} from '@netx/core-category-next/schema';

type Data = {[id: number]: ICategory};

type SubscriptionFn = (prevState: Data, nextState: Data) => void;

export type CategoriesStoreData = Record<number, ICategory | undefined>;

class CategoryStore {
	#callbacks = new Set<SubscriptionFn>();

	data = observable.object<Data>({});

	constructor() {
		makeObservable(this, {
			add: action,
			remove: action,
			update: action,
		});
	}

	add(categories: ICategory | ICategory[]) {
		const oldData = {...this.data};
		if (!Array.isArray(categories)) categories = [categories];
		categories.forEach((category) => {
			if (this.data[category.categoryid])
				return this.update(category.categoryid, category);
			this.data[category.categoryid] = category;
		});
		this.#publish(oldData, this.data);
	}

	remove(id: number) {
		const oldData = {...this.data};
		const cleanupChildren = (id: number) => {
			const category = this.data[id];
			if (!category) return;
			category.children.forEach(cleanupChildren);
			delete this.data[id];
		};
		cleanupChildren(id);
		delete this.data[id];
		this.#publish(oldData, this.data);
	}

	get(id: number) {
		return this.data[id];
	}

	update(id: number, data: ICategory) {
		const oldData = {...this.data};
		const category = this.data[id];
		if (!category) return;

		if (
			category.ancestors.join() !== data.ancestors.join() ||
			category.name !== data.name
		) {
			const correctAncestors = (
				id: number,
				newAncestors: number[],
				newPath: string,
			) => {
				const category = this.data[id];
				if (!category) return;
				category.children.forEach((childId) => {
					const child = this.data[childId];
					if (!child) return;
					this.data[childId] = {
						...this.data[childId],
						ancestors: newAncestors,
						path: `${newPath} >> ${child.name}`,
					};
					correctAncestors(
						childId,
						[childId].concat(newAncestors),
						`${newPath} >> ${child.name}`,
					);
				});
			};
			correctAncestors(id, [id].concat(data.ancestors), `${data.path}`);
		}

		this.data[id] = {...this.data[id], ...data};
		this.#publish(oldData, this.data);
	}

	subscribe(listener: SubscriptionFn) {
		this.#callbacks.add(listener);
		return () => {
			// remove the listener
			this.#callbacks.delete(listener);
		};
	}

	#publish(prevState: Data, nextState: Data) {
		if (prevState !== nextState) {
			this.#callbacks.forEach((listener) => listener(prevState, nextState));
		}
	}
}

export const categoriesStore = new CategoryStore();

export default categoriesStore;
